export interface PortalClaims {
  sub: string;
  email?: string;
  name?: string;
  "cognito:groups"?: string[] | string;
  exp?: number;
}

export interface PortalSession {
  accessToken: string;
  idToken?: string;
  expiresAt: number;
  claims: PortalClaims;
  mock?: boolean;
}

const STORAGE_KEY = "xbolo.portal.session";
const PKCE_KEY = "xbolo.portal.pkce";

type MockRole = "admin" | "partner";

export const authConfig: {
  issuer: string;
  clientId: string;
  domain: string;
  mock: boolean;
  mockRole: MockRole;
} = {
  issuer: import.meta.env.VITE_COGNITO_ISSUER || "",
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || "",
  domain: (import.meta.env.VITE_COGNITO_DOMAIN || "").replace(/\/$/, ""),
  mock: import.meta.env.VITE_AUTH_MOCK === "true",
  mockRole: import.meta.env.VITE_AUTH_MOCK_ROLE === "admin" ? "admin" : "partner"
};

export function getStoredSession(): PortalSession | null {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as PortalSession;
    if (session.expiresAt <= Date.now()) {
      clearSession();
      return null;
    }
    if (authConfig.mock && session.mock) {
      const expectedSession = createMockSession(authConfig.mockRole);
      const expectedGroups = JSON.stringify(expectedSession.claims["cognito:groups"]);
      const currentGroups = JSON.stringify(session.claims["cognito:groups"]);
      if (expectedGroups !== currentGroups) {
        storeSession(expectedSession);
        return expectedSession;
      }
    }
    return session;
  } catch {
    clearSession();
    return null;
  }
}

export function storeSession(session: PortalSession) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession() {
  window.localStorage.removeItem(STORAGE_KEY);
  window.sessionStorage.removeItem(PKCE_KEY);
}

export async function startLogin() {
  if (authConfig.mock) {
    const session = createMockSession(authConfig.mockRole);
    storeSession(session);
    window.location.reload();
    return;
  }

  if (!authConfig.domain || !authConfig.clientId) {
    throw new Error("Cognito ainda nao configurado.");
  }

  const verifier = randomString(64);
  const challenge = await pkceChallenge(verifier);
  const state = randomString(32);
  window.sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state }));

  const redirectUri = getRedirectUri();
  const params = new URLSearchParams({
    client_id: authConfig.clientId,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: redirectUri,
    state,
    lang: "pt-BR",
    code_challenge_method: "S256",
    code_challenge: challenge
  });

  window.location.assign(`${authConfig.domain}/oauth2/authorize?${params.toString()}`);
}

export async function completeLoginFromUrl(url: URL): Promise<PortalSession | null> {
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  if (!code) return null;

  const stored = JSON.parse(window.sessionStorage.getItem(PKCE_KEY) || "{}") as {
    verifier?: string;
    state?: string;
  };
  if (!stored.verifier || stored.state !== state) {
    throw new Error("Estado de login invalido.");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: authConfig.clientId,
    code,
    redirect_uri: getRedirectUri(),
    code_verifier: stored.verifier
  });

  const response = await fetch(`${authConfig.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body
  });
  if (!response.ok) throw new Error("Nao foi possivel concluir o login.");

  const token = await response.json();
  const claims = decodeJwt<PortalClaims>(token.id_token || token.access_token);
  const session: PortalSession = {
    accessToken: token.access_token,
    idToken: token.id_token,
    expiresAt: Date.now() + Number(token.expires_in || 3600) * 1000,
    claims
  };
  storeSession(session);
  window.sessionStorage.removeItem(PKCE_KEY);
  return session;
}

export function logout() {
  clearSession();
  if (!authConfig.mock && authConfig.domain && authConfig.clientId) {
    const params = new URLSearchParams({
      client_id: authConfig.clientId,
      logout_uri: window.location.origin
    });
    window.location.assign(`${authConfig.domain}/logout?${params.toString()}`);
    return;
  }
  window.location.assign(window.location.origin);
}

export function getGroups(claims: PortalClaims): string[] {
  const groups = claims["cognito:groups"];
  if (Array.isArray(groups)) return groups;
  if (typeof groups === "string") return groups.split(",").map((item) => item.trim()).filter(Boolean);
  return [];
}

export function isAdmin(claims: PortalClaims) {
  return getGroups(claims).includes("xbolo_admin");
}

export function isPartner(claims: PortalClaims) {
  return isAdmin(claims) || getGroups(claims).includes("xbolo_partner");
}

export function decodeJwt<T>(token: string): T {
  const [, payload] = token.split(".");
  const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
  const json = decodeURIComponent(
    atob(normalized)
      .split("")
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join("")
  );
  return JSON.parse(json) as T;
}

export function createMockSession(role: "admin" | "partner" = "partner"): PortalSession {
  const claims: PortalClaims = {
    sub: `mock-${role}`,
    email: `${role}@xbolo.local`,
    name: role === "admin" ? "Admin X-Bolo" : "Parceiro X-Bolo",
    "cognito:groups": role === "admin" ? ["xbolo_admin", "xbolo_partner"] : ["xbolo_partner"]
  };
  return {
    accessToken: "mock-token",
    expiresAt: Date.now() + 24 * 60 * 60 * 1000,
    claims,
    mock: true
  };
}

function getRedirectUri() {
  return `${window.location.origin}/auth/callback`;
}

function randomString(length: number) {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function pkceChallenge(verifier: string) {
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
